import React, { Dispatch, SetStateAction, useEffect, useMemo, useState, useRef } from "react";
import MenuItem from "@material-ui/core/MenuItem";
import InputBase from "@material-ui/core/InputBase";
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import EditIcon from '@material-ui/icons/Edit';
import Select from "@material-ui/core/Select";
import { withStyles } from "@material-ui/core/styles";
import { Field, Form, Formik, FieldProps } from "formik";
import { useSelector } from "react-redux";
import * as Yup from "yup";
import {
  BookingType,
  IBookingCreateInput,
  IRiskBasedPricingParams
} from "../../../../reducers/bookings/types";
import { IBranch, IServiceLocation } from "../../../../reducers/website/types";
import { getMaxPickupDateTime, getMinPickupDateTime, isPickupWithinLeadTime } from "../../../../utils/leadTime";
import { IAppState } from "../../../../store";
import FlatPickerBar from "../../../FlatPicker";
import styles from './index.module.css';
import SearchIcon from "@material-ui/icons/SearchRounded";
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import Container from "@material-ui/core/Container";
import cx from 'classnames';
import Checkbox from "@material-ui/core/Checkbox";
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import { getEndDateForBilling, RATE_TYPES } from '../../utils';
import { DateTime as d, Settings } from "luxon"
import dayjs from "dayjs";
import { getLocalizedAuthorisedSignatorySyntex, getLocalizedDateFormat, getLocalizedDateFormatString } from "../../../../utils/localized.syntex";
import { DATE_TYPE } from "../../../utils";
import { Box, Card, CardContent, Collapse, Divider, IconButton, InputAdornment, makeStyles, Popper, TextField, Tooltip, Typography, useMediaQuery } from "@material-ui/core";
import { Alert, Autocomplete } from "@material-ui/lab";
import { useTranslation } from "react-i18next";
import { CheckBox } from "@material-ui/icons";
import { useHistory, useLocation } from "react-router-dom";
import { getNameForAuthorizedSignatory, searchLocations } from "../utils";
import { Clear, NearMeTwoTone } from "@material-ui/icons";
import { debounce } from "lodash";
import { UserRole } from "../../../../reducers/auth/types";

const CURRENT_LOCATION_OPTION_ID = "__USE_CURRENT_LOCATION__";

const currentLocationOption = {
  id: CURRENT_LOCATION_OPTION_ID,
  name: "Use current location",
  address: { country: "" }
} as IServiceLocation;


const PlainInput = withStyles((theme) => ({
  root: {
    'label + &': {
      marginTop: 0,
    },
    border: 0,
    opacity: 0
  },
  input: {
    border: 0,
    paddingLeft: 5,
    borderRadius: 5,
    fontSize: 'var(--font-medium)',
    minHeight: 0,
    padding: '5px 5px 8px 5px',
    '&:hover': {
      backgroundColor: 'none'
    },
    '&:focus': {
      borderRadius: 5,
      ackgroundColor: 'none'
    },
  },
}))(InputBase);

const useStyles = makeStyles((theme) => ({
  searchScreenGridContainer: {
    padding: 0,
    [theme.breakpoints.down('md')]: {
      padding: 10,
    },
  }
}))
interface IProps {
  bookingData: IBookingCreateInput;
  requiredFieldUpdated: boolean;
  setRequiredFieldUpdated: Dispatch<SetStateAction<boolean>>
  searchVehicles: (data: IBookingCreateInput) => void;
  position?: number;
  height?: number;
  vehiclesSearched?: boolean;
  resetSearch: () => void;
  isBookingUpdate?: boolean
  setValues: Dispatch<SetStateAction<IBookingCreateInput>>
  values: IBookingCreateInput;
  handleSelectionBack: () => void
  bookingType?: string;
}
const isSelectOpenInitial: boolean[] = [false, false, false, false, false, false, false, false];

interface IOperatingHours {
  pickUp: number[],
  dropOff: number[],
}

const BaseDetails: React.FC<IProps> = (props) => {
  const location = useLocation();
  const history = useHistory();
  const [differentDropOffLocation, setDifferentDropOffLocation] = useState<boolean>(false);
  const { values, setValues } = props
  const [isSelectOpen, setSelectOpen] = useState<boolean[]>(isSelectOpenInitial);
  const [vehicleSearched, setVehicleSearched] = useState<boolean>(false);
  const [currentBranch, setCurrentBranch] = useState<IBranch>();
  const [searchDisabled, setSearchDisabled] = useState<boolean>(false)
  const { setRequiredFieldUpdated, position, height } = props;
  const [operatingHours, setOperatingHours] = useState<IOperatingHours>({
    pickUp: [0, 23.45],
    dropOff: [0, 23.45]
  })
  const website = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite);
  const user = useSelector((state: IAppState) => state.authReducer.user);
  const [riskBasedPricingEnabled, setRiskBasedPricingEnabled] = useState<boolean>(false);
  const isMobile = useMediaQuery('(max-width:959px)');
  const { allowSubscriptionBookings, closeGroupSharingEnabled } = website.organisation
  const { country } = website.organisation.address;
  const branchData = website.branches;

  const leadTimeSettings = useMemo(() => ({
    minLeadTimeOptIn: website.minLeadTimeOptIn,
    minLeadTimeValue: website.minLeadTimeValue,
    minLeadTimeUnit: website.minLeadTimeUnit,
    maxLeadTimeOptIn: website.maxLeadTimeOptIn,
    maxLeadTimeValue: website.maxLeadTimeValue,
    maxLeadTimeUnit: website.maxLeadTimeUnit,
  }), [website]);

  const minPickupDateTime = useMemo(
    () => getMinPickupDateTime(leadTimeSettings, currentBranch?.timeZone),
    [leadTimeSettings, currentBranch?.timeZone]
  );

  const maxPickupDateTime = useMemo(
    () => getMaxPickupDateTime(leadTimeSettings, currentBranch?.timeZone),
    [leadTimeSettings, currentBranch?.timeZone]
  );

  const baseLocations = useMemo(() => {
    return branchData
      .flatMap((branch) => branch.activeLocations)
      .sort((a, b) =>
        a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      );
  }, [branchData]);
  const [displayLocations, setDisplayLocations] = useState<IServiceLocation[]>([]);

  useEffect(() => {
    setDisplayLocations(baseLocations);
  }, [baseLocations]);
  const { t } = useTranslation();
  const [showSearchError, setSearchError] = useState({
    show: false,
    message: ""
  });
  const classes = useStyles()
  const clearSearchError = () => {
    setSearchError({
      show: false,
      message: ""
    });
  }

  useEffect(() => {
    const selectedBranch = branchData.find((branch) => branch.id === values.branchId);
    if (selectedBranch) {
      setCurrentBranch(selectedBranch);
      Settings.defaultZone = selectedBranch.timeZone || "Europe/London"
    }
  }, [values.branchId])

  useEffect(() => {
    if (props.bookingData) {
      if (props.bookingData.isSubscription) {
        props.bookingData.billingFrequency = 1
        props.bookingData.isRecurringBilling = true
        props.bookingData.rateTypeDuration = props.bookingData.rateTypeName === RATE_TYPES.WEEKLY ? 10080 : 43200
      }
      setValues(props.bookingData);
      if (props.bookingData.dropoffServiceLocation && props.bookingData.dropoffServiceLocation.length) {
        setDifferentDropOffLocation(
          props.bookingData.pickupServiceLocation !==
          props.bookingData.dropoffServiceLocation
        );
      }
      else {
        setDifferentDropOffLocation(false);
      }
    }
  }, [props.bookingData]);

  useEffect(() => {
    if (props.isBookingUpdate && props.bookingData.dropoffDateTime) {
      const pickupOperatingHours = getOperatingHours("PICKUP", d.fromISO(props.bookingData.pickupDateTime));
      const dropoffOperatingHours = getOperatingHours("DROPOFF", d.fromISO(props.bookingData.dropoffDateTime));
      setOperatingHours({
        pickUp: pickupOperatingHours,
        dropOff: dropoffOperatingHours,
      });
    }
  }, [props.isBookingUpdate, currentBranch])

  useEffect(() => {
    if (props.isBookingUpdate && values.dropoffDateTime && values.rateTypeName) {
      const endDate = getEndDateForBilling(values.pickupDateTime, values.rateTypeName)
      let _disableSearch = d.fromISO(values.dropoffDateTime) < endDate;
      if (closeGroupSharingEnabled || (website.organisation.enableBookingCreationForApprovedDriver && user?.role == UserRole.DRIVER)) {
        _disableSearch = false;
      }
      setSearchDisabled(_disableSearch);
    }
  }, [
    props.isBookingUpdate,
    values.dropoffDateTime,
    values.rateTypeName,
    values.pickupDateTime,
    values.isKeylessEntry,
    values.isCarShare,
    closeGroupSharingEnabled,
    website.organisation.enableBookingCreationForApprovedDriver
  ]);

  useEffect(() => {
    if (website.allowedRateTypes?.length && !values.isSubscription && !props.isBookingUpdate) {
      let hourlyAllowed = false;
      let dailyAllowed = false;
      let weeklyAllowed = false;
      let monthlyAllowed = false;
      website.allowedRateTypes.forEach((rateType) => {
        switch (rateType) {
          case RATE_TYPES.HOURLY:
            hourlyAllowed = true;
            break;
          case RATE_TYPES.DAILY:
            dailyAllowed = true;
            break;
          case RATE_TYPES.WEEKLY:
            weeklyAllowed = true;
            break;
          case RATE_TYPES.MONTHLY:
            monthlyAllowed = true;
            break;
          default:
            dailyAllowed = true;
            break;
        }
      })
      if (values.dropoffDateTime) {
        // find booking duration in days, 0 means less than 1 day
        const bookingDurationInDays = Math.floor(d.fromISO(values.dropoffDateTime).diff(d.fromISO(values.pickupDateTime), "days").toObject().days || 0)
        if (!bookingDurationInDays) {
          if (hourlyAllowed) {
            setValues({
              ...values,
              rateTypeDuration: 60,
              billingCycleDuration: 60,
              rateTypeName: RATE_TYPES.HOURLY,
              billingCycleName: RATE_TYPES.HOURLY,
              isRecurringBilling: false,
              billingFrequency: 0
            })
          } else {
            setValues({
              ...values,
              rateTypeDuration: 1440,
              billingCycleDuration: 1440,
              rateTypeName: RATE_TYPES.DAILY,
              billingCycleName: RATE_TYPES.DAILY,
              isRecurringBilling: false,
              billingFrequency: 0
            })
          }
        } else {
          let now = new Date();
          const daysInCurrentMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();

          if (bookingDurationInDays >= daysInCurrentMonth) { // more than a month booking
            if (monthlyAllowed) { // check if monthly allowed
              setValues({
                ...values,
                rateTypeDuration: 43200,
                billingCycleDuration: 43200,
                rateTypeName: RATE_TYPES.MONTHLY,
                billingCycleName: RATE_TYPES.MONTHLY,
                isRecurringBilling: true,
                billingFrequency: 1
              });
            } else if (weeklyAllowed) { // check if weekly allowed
              setValues({
                ...values,
                rateTypeDuration: 10080,
                billingCycleDuration: 10080,
                rateTypeName: RATE_TYPES.WEEKLY,
                billingCycleName: RATE_TYPES.WEEKLY,
                isRecurringBilling: true,
                billingFrequency: 1
              });
            } else if (dailyAllowed) { // if not monthly or weekly allowed put it as a daily booking
              setValues({
                ...values,
                rateTypeDuration: 1440,
                billingCycleDuration: 1440,
                rateTypeName: RATE_TYPES.DAILY,
                billingCycleName: RATE_TYPES.DAILY,
                isRecurringBilling: false,
                billingFrequency: 0
              })
            }
          } else if (bookingDurationInDays >= 7) { // less than a month booking but more than a week
            if (weeklyAllowed) {
              setValues({
                ...values,
                rateTypeDuration: 10080,
                billingCycleDuration: 10080,
                rateTypeName: RATE_TYPES.WEEKLY,
                isRecurringBilling: true,
                billingCycleName: RATE_TYPES.WEEKLY,
                billingFrequency: 1
              });
            } else { // if not monthly or weekly allowed put it as a daily booking
              setValues({
                ...values,
                rateTypeDuration: 1440,
                billingCycleDuration: 1440,
                rateTypeName: RATE_TYPES.DAILY,
                billingCycleName: RATE_TYPES.DAILY,
                isRecurringBilling: false,
                billingFrequency: 0
              })
            }
          } else { // less than a week booking
            setValues({
              ...values,
              rateTypeDuration: 1440,
              billingCycleDuration: 1440,
              rateTypeName: RATE_TYPES.DAILY,
              billingCycleName: RATE_TYPES.DAILY,
              isRecurringBilling: false,
              billingFrequency: 0
            })
          }
        }
      }
    }
    // if (website?.organisation?.autoRateType) {
    //   if (values.dropoffDateTime) {
    //     const monthlyEndate = getEndDateForBilling(values.pickupDateTime, RATE_TYPES.MONTHLY)
    //     if (values.dropoffDateTime && values.dropoffDateTime >= monthlyEndate) {
    //       setValues({
    //         ...values,
    //         rateTypeDuration: 43200,
    //         rateTypeName: RATE_TYPES.MONTHLY,
    //         isRecurringBilling: true,
    //         billingFrequency: 1
    //       });
    //     } else {
    //       const weeklyEndDate = getEndDateForBilling(values.pickupDateTime, RATE_TYPES.WEEKLY);
    //       if (values.dropoffDateTime && values.dropoffDateTime >= weeklyEndDate) {
    //         setValues({
    //           ...values,
    //           rateTypeDuration: 10080,
    //           rateTypeName: RATE_TYPES.WEEKLY,
    //           isRecurringBilling: true,
    //           billingFrequency: 1
    //         });
    //       } else {
    //         setValues({
    //           ...values,
    //           rateTypeDuration: 1440,
    //           rateTypeName: RATE_TYPES.DAILY,
    //           isRecurringBilling: false,
    //           billingFrequency: 0
    //         })
    //       }
    //     }
    //   }
    // }
  }, [values.dropoffDateTime, values.pickupDateTime, website, values.defaultBillingCycle]);

  const hasTriggeredRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const pickupServiceLocation = params.get("pickup_location");
    const pickupDateTime = params.get("pickup_datetime");
    const dropoffDateTime = params.get("dropoff_datetime");

    if (!pickupServiceLocation || !pickupDateTime || !dropoffDateTime) return;
    if (hasTriggeredRef.current) return;

    const formValues: IBookingCreateInput = {
      ...values,
      pickupServiceLocation,
      dropoffServiceLocation:
        params.get("dropoff_location") || pickupServiceLocation,
      pickupDateTime,
      dropoffDateTime,
      branchId: params.get("branch_id") || "",
      billingCycleDuration: params.get("billing_cycle")
        ? Number(params.get("billing_cycle"))
        : undefined,
      authorizedSignatory: params.get("authorized_signatory") || "",
      isKeylessEntry: params.get("is_keyless") === "true",
      pcoNumber: params.get("pco") === "true",
      riskBasedPricingParams: {
        driverAge: Number(params.get("risk_based_driver_age") || 0),
        licenceDuration: Number(params.get("risk_based_licence_duration") || 0),
        penaltyPoints: Number(params.get("risk_based_penalty_points") || 0),
        insuranceClaimsHistory: Number(params.get("risk_based_insurance_claims") || 0)
      }
    };

    setValues(formValues);
    props.searchVehicles(formValues);

    hasTriggeredRef.current = true;
    setVehicleSearched(true);
  }, [location.search]);

  useEffect(() => {
    if (props.requiredFieldUpdated) {
      props.resetSearch()
    }
  }, [props.requiredFieldUpdated])

  useEffect(() => {
    if (values.pickupServiceLocation) {
      const branch = getBranch(values.pickupServiceLocation);
      setRiskBasedPricingEnabled(branch?.riskBasedPricingEnabled ?? false)
    }
  }, [values.pickupServiceLocation])

  const baseDetailsSchema = Yup.object().shape({
    dropoffDateTime: Yup.string(),
    dropoffServiceLocation: Yup.string(),
    pickupDateTime: Yup.string()
      .required()
      .min(1)
      .test("lead-time", t("root:invalidPickupLeadTime"), (value) => {
        if (!value) {
          return false;
        }
        return isPickupWithinLeadTime(value, leadTimeSettings, currentBranch?.timeZone);
      }),
    pickupServiceLocation: Yup.string()
      .required()
      .min(1)
  });

  const getBranchId = (id: string) => {
    const LN = baseLocations.find((location: any) => location.id === id);
    return (LN?.branch as any).id;
  }

  const handleSelectToggle = (i: number, open?: boolean) => {
    const selectOpenState = [...isSelectOpen];
    selectOpenState[i] =
      typeof open === "boolean" ? open : !selectOpenState[i];
    setSelectOpen(selectOpenState);
  };

  const getLocationName = (id: string, includeBranchName: boolean = true) => {
    if (id === "") {
      return t("root:chooseLocation");
    }
    const location = baseLocations.find((location: any) => location.id === id);
    if (!location) return "";
    return includeBranchName
      ? `${location.name} - ${(location.branch as any).name}`
      : location.name;
  }

  const getBranch = (id: string) => {
    const location = baseLocations.find((location: any) => location.id === id);
    return location?.branch as IBranch;
  }

  // Helper: convert decimal hour number (e.g. 9.15) to "HH:mm" and back
  const numberToHour = (opHr: number) => {
    const minute = parseInt((Math.round(opHr * 100)).toString().slice(-2));
    const hour = Math.floor(opHr);
    return `${hour > 9 ? hour : `0${hour}`}:${minute > 9 ? minute : `0${minute}`}`;
  };

  const hourToNumber = (hour: string) => {
    const [hh, mm] = hour.split(":");
    return parseInt(hh, 10) + (parseInt(mm, 10) / 100);
  };

  const roundOffToNextQuarter = (time: string) => {
    const [hhStr, mmStr] = time.split(":");
    const hour = parseInt(hhStr, 10);
    const minute = parseInt(mmStr, 10);
    const nextQuarter = (Math.floor(minute / 15) + 1) * 15;
    if (nextQuarter < 60) return hour + nextQuarter / 100;
    return hour + 1; // next hour as decimal
  };

  const setTimeOnDate = (date: any /* luxon DateTime */, hour: number) => {
    const formattedDate = date.toFormat("dd/MM/yyyy");
    return d.fromFormat(`${formattedDate} ${numberToHour(hour)}`, "dd/MM/yyyy HH:mm");
  };

  // Return operating hours numeric pair for given type and date
  const getOperatingHours = (hoursFor: "PICKUP" | "DROPOFF", date: any /* DateTime */) => {
    const branchNow = d.now().setZone(currentBranch?.timeZone || "Europe/London");
    const isToday = branchNow.hasSame(date, "day");
    const nextAvailableHour = roundOffToNextQuarter(branchNow.toFormat("HH:mm"));
    let _hours: number[] = [0, 23.45];

    if (hoursFor === "PICKUP") {
      if (date.startOf("day") < minPickupDateTime.startOf("day")) {
        return [0, 0];
      }
      if (maxPickupDateTime && date.startOf("day") > maxPickupDateTime.startOf("day")) {
        return [0, 0];
      }
    }

    if (!currentBranch || !currentBranch.operatingHours?.length) return _hours;

    const op = currentBranch.operatingHours.find((o: any) => o.dayIndex === date.weekday);
    if (!op) return _hours;

    const startHour = hoursFor === "PICKUP" ? hourToNumber(op.pickupFromTime || "00:00") : hourToNumber(op.returnFromTime || "00:00");
    const endHour = hoursFor === "PICKUP" ? hourToNumber(op.pickupToTime || "00:00") : hourToNumber(op.returnToTime || "00:00");

    // Handle closed + dropoff logic
    if (op.closed) {
      if (hoursFor === "PICKUP") return [0, 0];
      if (hoursFor === "DROPOFF" && !op.allowDropOffs) return [0, 0];
    }

    let effectiveStart = startHour;
    // If the user asks for hours on today, exclude expired times
    if (isToday) {
      // nextAvailableHour is in decimal form (e.g. 12.15). Ensure we don't allow times < now.
      if (nextAvailableHour > effectiveStart) {
        effectiveStart = nextAvailableHour;
      }
      // If the end is already before now, no hours available
      if (endHour < nextAvailableHour) return [0, 0];
    }

    if (hoursFor === "PICKUP" && date.hasSame(minPickupDateTime, "day")) {
      const minLeadHour = roundOffToNextQuarter(minPickupDateTime.toFormat("HH:mm"));
      if (minLeadHour > effectiveStart) {
        effectiveStart = minLeadHour;
      }
      if (endHour < effectiveStart) return [0, 0];
    }

    _hours = [effectiveStart, endHour];

    // If this is a dropoff on the same day as pickup, we must ensure the dropoff window doesn't go before pickup
    if (hoursFor === "DROPOFF" && values.pickupDateTime) {
      const pickupDt = d.fromISO(values.pickupDateTime);
      if (pickupDt.hasSame(date, "day")) {
        const pickupHour = hourToNumber(pickupDt.toFormat("HH:mm"));
        // if endHour <= pickupHour then we can't dropoff later than pickup: cap endHour to pickupHour
        if (endHour <= pickupHour) {
          _hours = [effectiveStart, pickupHour];
        }
      }
    }

    return _hours;
  };

  // Find next available day (with a valid dropoff window) starting from given date (inclusive or exclusive behaviour
  // in call). Returns a DateTime with time set to that day's returnFrom time (and respecting today's now if needed).
  const getNextAvailableDay = (dateTime: any /* DateTime */) => {
    let candidate = dateTime.startOf("day");
    const disabledDates = getDisabledDates("DROPOFF");

    // start searching from the given date (useful when we've already determined that the time on that date
    // is outside window and need the next available day). We'll try candidate then candidate+1...
    for (let i = 0; i < 14; i++) {
      // If candidate is disabled by weekday or by localized date string, skip
      const localized = getLocalizedDateFormat(country, candidate.toUTC().toISO(), DATE_TYPE.CONDENSED);
      if (disabledDates.includes(candidate.weekday) || disabledDates.includes(localized)) {
        candidate = candidate.plus({ days: 1 });
        continue;
      }

      const op = currentBranch?.operatingHours?.find((o: any) => o.dayIndex === candidate.weekday);
      if (!op || op.closed) {
        candidate = candidate.plus({ days: 1 });
        continue;
      }

      // Determine the earliest usable returnFrom for this candidate day
      let returnFrom = hourToNumber(op.returnFromTime || "00:00");
      const returnTo = hourToNumber(op.returnToTime || "00:00");

      // If candidate is today, ensure we don't pick a time earlier than now
      if (candidate.hasSame(d.now(), "day")) {
        const nowRounded = roundOffToNextQuarter(d.now().toFormat("HH:mm"));
        if (nowRounded > returnFrom) returnFrom = nowRounded;
        if (returnTo <= nowRounded) {
          // today's window expired
          candidate = candidate.plus({ days: 1 });
          continue;
        }
      }

      // If return window is degenerate (returnFrom === returnTo) treat as single-slot at returnFrom
      if (returnTo < returnFrom) {
        // No usable window on this day
        candidate = candidate.plus({ days: 1 });
        continue;
      }

      // We found a candidate day. Set its time to returnFrom and return.
      const result = setTimeOnDate(candidate, returnFrom);
      // Ensure result is not before input dateTime (for cases where we started searching with same-day but desired time is later)
      if (result < dateTime) {
        // If result earlier than requested, choose result only if that still respects booking duration elsewhere.
        // We'll return it; callers may compare with pickup.
      }

      // Update operatingHours state for dropOff to this day's hours (consumer expects this)
      setOperatingHours((prev: any) => ({
        ...prev,
        dropOff: [returnFrom, returnTo]
      }));

      return result;
    }

    // Fallback: return next day at 00:00 if nothing found in window (very unlikely)
    const fallback = dateTime.plus({ days: 1 }).startOf("day");
    setOperatingHours((prev: any) => ({ ...prev, dropOff: [0, 0] }));
    return fallback;
  };

  // Handle pickup date selection
  const handlePickUpDate = (value: Date) => {
    clearSearchError();
    const fromDate = d.fromJSDate(value).startOf("day");

    // Pickup hours for the selected date
    const _pickUpHours = getOperatingHours("PICKUP", fromDate);
    // set pickup time to earliest available pickup hour on that date
    const pickupDateTime = setTimeOnDate(fromDate, _pickUpHours[0]).toUTC().toISO();

    const minBookingDuration = currentBranch && currentBranch.minBookingDurationOptIn && currentBranch.minBookingDuration ? currentBranch.minBookingDuration : 1;
    let pickupDtLuxon = d.fromISO(pickupDateTime);

    // Desired dropoff (initial calculation)
    let desiredDropoff = minBookingDuration > 0 ? pickupDtLuxon.plus({ hours: minBookingDuration }) : pickupDtLuxon.plus({ days: 1 });

    // If user already had a dropoff and it's after desiredDropoff, we'll keep it; otherwise compute
    let defaultDropOffDate = values.dropoffDateTime ? d.fromISO(values.dropoffDateTime) : null;

    if (!defaultDropOffDate || defaultDropOffDate < desiredDropoff) {
      // Walk through candidate days starting from desiredDropoff day and find where time fits into return window
      let candidateDate = desiredDropoff;
      let computedDropoff: any = null;
      for (let i = 0; i < 14; i++) {
        const disabledDates = getDisabledDates("DROPOFF");
        const localized = getLocalizedDateFormat(country, candidateDate.toUTC().toISO(), DATE_TYPE.CONDENSED);
        if (disabledDates.includes(candidateDate.weekday) || disabledDates.includes(localized)) {
          candidateDate = candidateDate.plus({ days: 1 }).startOf("day");
          continue;
        }

        const op = currentBranch?.operatingHours?.find((o: any) => o.dayIndex === candidateDate.weekday);
        if (!op || op.closed) {
          candidateDate = candidateDate.plus({ days: 1 }).startOf("day");
          continue;
        }

        // Determine return window for candidate day, and respect "now" if it's today
        let returnFrom = hourToNumber(op.returnFromTime || "00:00");
        const returnTo = hourToNumber(op.returnToTime || "00:00");

        if (candidateDate.hasSame(d.now(), "day")) {
          const nowRounded = roundOffToNextQuarter(d.now().toFormat("HH:mm"));
          if (nowRounded > returnFrom) returnFrom = nowRounded;
          // If entire today's window expired, skip
          if (returnTo <= nowRounded) {
            candidateDate = candidateDate.plus({ days: 1 }).startOf("day");
            continue;
          }
        }

        // If desiredDropoff falls before this day's returnFrom, snap to returnFrom
        if (desiredDropoff < setTimeOnDate(candidateDate, returnFrom)) {
          computedDropoff = setTimeOnDate(candidateDate, returnFrom);
          // If computedDropoff is still < pickup + minBookingDuration, we need to ensure duration satisfied
          if (computedDropoff < pickupDtLuxon.plus({ hours: minBookingDuration })) {
            // If computed dropoff would violate min duration, go to next day
            candidateDate = candidateDate.plus({ days: 1 }).startOf("day");
            desiredDropoff = pickupDtLuxon.plus({ hours: minBookingDuration });
            continue;
          }
          break;
        }

        // If desiredDropoff inside return window, use it
        const desiredTimeNum = hourToNumber(desiredDropoff.toFormat("HH:mm"));
        if (desiredTimeNum >= returnFrom && desiredTimeNum <= returnTo) {
          computedDropoff = desiredDropoff;
          break;
        }

        // If desiredDropoff > returnTo, move to next candidate day
        if (desiredDropoff > setTimeOnDate(candidateDate, returnTo)) {
          candidateDate = candidateDate.plus({ days: 1 }).startOf("day");
          desiredDropoff = candidateDate; // will be re-evaluated
          continue;
        }

        candidateDate = candidateDate.plus({ days: 1 }).startOf("day");
      }

      if (!computedDropoff) {
        // fallback to next available day at its returnFrom
        computedDropoff = getNextAvailableDay(desiredDropoff);
      }

      defaultDropOffDate = computedDropoff;
    } else {
      // existing dropoff is after desired dropoff, keep existing but ensure it fits window, else adjust
      const opHr = currentBranch?.operatingHours?.find((op: any) => op.dayIndex === defaultDropOffDate!.weekday);
      if (opHr) {
        const _dropOffHours = getOperatingHours("DROPOFF", defaultDropOffDate);
        if (defaultDropOffDate
          && (hourToNumber(defaultDropOffDate.toFormat("HH:mm")) < _dropOffHours[0]
            || hourToNumber(defaultDropOffDate.toFormat("HH:mm")) > _dropOffHours[1])) {
          defaultDropOffDate = getNextAvailableDay(defaultDropOffDate);
        }
      }
    }

    // Final safety checks: ensure defaultDropOffDate respects minBookingDuration
    if (minBookingDuration > 0 && defaultDropOffDate && defaultDropOffDate < pickupDtLuxon.plus({ hours: minBookingDuration })) {
      defaultDropOffDate = pickupDtLuxon.plus({ hours: minBookingDuration });
      // If this exceeds the day's returnTo, find next available
      const _dropHours = getOperatingHours("DROPOFF", defaultDropOffDate);
      if (hourToNumber(defaultDropOffDate.toFormat("HH:mm")) > _dropHours[1]) {
        defaultDropOffDate = getNextAvailableDay(defaultDropOffDate);
      }
    }

    // If final dropoff still not in dropoff hours for that date, pick next available
    const _dropOffHours = getOperatingHours("DROPOFF", defaultDropOffDate);
    if (hourToNumber(defaultDropOffDate!.toFormat("HH:mm")) < _dropOffHours[0] || hourToNumber(defaultDropOffDate!.toFormat("HH:mm")) > _dropOffHours[1]) {
      defaultDropOffDate = getNextAvailableDay(defaultDropOffDate);
    }

    const dropoffDateTime = defaultDropOffDate!.toUTC().toISO();

    setValues({
      ...values,
      pickupDateTime,
      dropoffDateTime,
    });

    setOperatingHours({
      pickUp: _pickUpHours,
      dropOff: _dropOffHours,
    });

    setRequiredFieldUpdated(true);
    setVehicleSearched(false)
  };

  // Handle pickup time change — adjust dropoff respecting windows and min duration
  const handlePickUpTime = (event: any) => {
    clearSearchError();
    const selectedTime = hourToNumber(event.target.value);
    const pickupDate = d.fromISO(values.pickupDateTime);
    const pickupDateTime = setTimeOnDate(pickupDate, selectedTime);

    const minBookingDuration = currentBranch && currentBranch.minBookingDurationOptIn && currentBranch.minBookingDuration ? currentBranch.minBookingDuration : 0;
    let dropoffDateTime = values.dropoffDateTime ? d.fromISO(values.dropoffDateTime) : pickupDateTime.plus({ hours: minBookingDuration || 1 });

    // Compute earliest dropoff candidate satisfying minBookingDuration
    if (minBookingDuration > 0) {
      const earliest = pickupDateTime.plus({ hours: minBookingDuration });
      if (dropoffDateTime < earliest) dropoffDateTime = earliest;
    }

    // Determine dropoff operating hours for the dropoff's day
    let _dropOffHours = getOperatingHours("DROPOFF", dropoffDateTime);

    const isSameDay = pickupDateTime.hasSame(dropoffDateTime, "day");

    // compute a preferred dropoff time relative to selected pickup (add min duration or next quarter)
    let preferredOffset = parseInt(event.target.value.split(":")[1], 10) === 45 ? selectedTime + 0.55 : selectedTime + 0.15;
    if (minBookingDuration > 0) preferredOffset = selectedTime + minBookingDuration;

    // If same day, try keep dropoff on same day if within window
    if (isSameDay) {
      if (_dropOffHours[0] <= preferredOffset && _dropOffHours[1] >= preferredOffset) {
        dropoffDateTime = setTimeOnDate(dropoffDateTime, preferredOffset);
      } else if (preferredOffset > _dropOffHours[1]) {
        // preferred beyond today's returnTo -> go to next available day
        dropoffDateTime = getNextAvailableDay(pickupDateTime);
      } else if (preferredOffset < _dropOffHours[0]) {
        // preferred before returnFrom -> snap to returnFrom
        dropoffDateTime = setTimeOnDate(dropoffDateTime, _dropOffHours[0]);
        // but ensure min duration still satisfied
        if (minBookingDuration > 0 && dropoffDateTime < pickupDateTime.plus({ hours: minBookingDuration })) {
          dropoffDateTime = getNextAvailableDay(pickupDateTime);
        }
      }
    } else {
      // Not same day: validate dropoff falls in its day window, otherwise find next
      if (hourToNumber(dropoffDateTime.toFormat("HH:mm")) < _dropOffHours[0]) {
        dropoffDateTime = setTimeOnDate(dropoffDateTime, _dropOffHours[0]);
      }
      if (hourToNumber(dropoffDateTime.toFormat("HH:mm")) > _dropOffHours[1]) {
        dropoffDateTime = getNextAvailableDay(dropoffDateTime);
      }
    }

    // Final re-check: if dropoff now violates minBookingDuration, adjust
    const timeDiff = dropoffDateTime.diff(pickupDateTime, 'hours').toObject().hours || 0;
    if (timeDiff < minBookingDuration) {
      dropoffDateTime = dropoffDateTime.plus({ hours: minBookingDuration - timeDiff });
      if (hourToNumber(dropoffDateTime.toFormat("HH:mm")) > _dropOffHours[1]) {
        dropoffDateTime = getNextAvailableDay(dropoffDateTime);
      }
    }

    setValues({
      ...values,
      pickupDateTime: pickupDateTime.toUTC().toISO(),
      dropoffDateTime: dropoffDateTime.toUTC().toISO(),
    });

    setRequiredFieldUpdated(true);
    setVehicleSearched(false)
  };

  // Handle dropoff date selection — ensure chosen time sits in window, else shift to nearest available
  const handleDropoffDate = (value: Date) => {
    clearSearchError();
    const toDate = d.fromJSDate(value).startOf("day");
    const _dropOffHours = getOperatingHours("DROPOFF", toDate);
    let dropoffDtLuxon = setTimeOnDate(toDate, _dropOffHours[0]);

    // If there's an existing pickup ensure min booking satisfied
    const minBookingDuration = currentBranch && currentBranch.minBookingDurationOptIn && currentBranch.minBookingDuration ? currentBranch.minBookingDuration : 0;
    const pickupDt = values.pickupDateTime ? d.fromISO(values.pickupDateTime) : null;
    if (pickupDt && minBookingDuration > 0 && dropoffDtLuxon < pickupDt.plus({ hours: minBookingDuration })) {
      dropoffDtLuxon = pickupDt.plus({ hours: minBookingDuration });
      // If now outside this day's window move to next
      if (hourToNumber(dropoffDtLuxon.toFormat("HH:mm")) > _dropOffHours[1]) {
        dropoffDtLuxon = getNextAvailableDay(dropoffDtLuxon);
      }
    }

    // final guard — if dropoff time still outside window then pick next available slot
    if (hourToNumber(dropoffDtLuxon.toFormat("HH:mm")) < _dropOffHours[0] || hourToNumber(dropoffDtLuxon.toFormat("HH:mm")) > _dropOffHours[1]) {
      dropoffDtLuxon = getNextAvailableDay(dropoffDtLuxon);
    }

    const dropoffDateTime = dropoffDtLuxon.toUTC().toISO();
    setValues({ ...values, dropoffDateTime });
    setOperatingHours((prev: any) => ({ ...prev, dropOff: _dropOffHours }));
    setRequiredFieldUpdated(true);
    setVehicleSearched(false);
  };  

  const getTimesArray = (opHr: number[]) => {
    let arr = [];
    for (let i = opHr[0]; i <= opHr[1]; i) {
      arr.push(numberToHour(i));
      const minute = parseInt((Math.round(i * 100)).toString().substr(-2));
      if (minute >= 45)
        i = Math.floor(i) + 1;
      else
        i += 0.15
    }
    const endStr = numberToHour(opHr[1]);
    if (!arr.includes(endStr)) {
      arr.push(endStr);
    }
    return arr
  }  

  function getHolidays(startDate: d, endDate: d) {
    const datesArray = [];
    let dateIterator = startDate;
    while (dateIterator < endDate) {
      datesArray.push(getLocalizedDateFormat(country, dateIterator.toUTC().toISO(), DATE_TYPE.CONDENSED));
      dateIterator = dateIterator.plus({ days: 1 });
    }
    datesArray.push(getLocalizedDateFormat(country, endDate.toUTC().toISO(), DATE_TYPE.CONDENSED));
    return datesArray;
  }

  const handleRiskBasedPricingParamsChange = (field: string, value: number) => {
    setValues(prevValue => ({
      ...prevValue,
      riskBasedPricingParams: {
        ...(prevValue.riskBasedPricingParams || {}),
        [field]: value
      }
    }))
    if (vehicleSearched) setVehicleSearched(false);
    if (searchDisabled) setSearchDisabled(false);
  }

  const getDisabledDates = (type: "PICKUP" | "DROPOFF") => {
    let disabledDatesArray: (number | string)[] = [];

    if (currentBranch?.operatingHours?.length) {
      for (const opHr of currentBranch.operatingHours) {
        if (type === "PICKUP") {
          if (opHr.closed || opHr.allowDropOffs) {
            disabledDatesArray.push(opHr.dayIndex);
          }
        } else if (type === "DROPOFF") {
          if (opHr.allowDropOffs) {
            continue;
          }
          if (opHr.closed && !opHr.allowDropOffs) {
            disabledDatesArray.push(opHr.dayIndex);
          }
        }
      }
    }

    if (currentBranch?.holidays?.length) {
      for (const holiday of currentBranch.holidays) {
        const dates = getHolidays(
          d.fromFormat(holiday.startDate, getLocalizedDateFormatString(country, DATE_TYPE.CONDENSED)),
          d.fromFormat(holiday.endDate, getLocalizedDateFormatString(country, DATE_TYPE.CONDENSED))
        );
        if (type === "PICKUP") {
          disabledDatesArray = [...disabledDatesArray, ...dates];
        } else if (!holiday.allowDropOffs) {
          disabledDatesArray = [...disabledDatesArray, ...dates];
        }
      }
    }

    return disabledDatesArray;
  };

  const handleZipcodeSearch = React.useMemo(
    () =>
      debounce(async (input: string) => {
        if (!input) {
          setDisplayLocations(baseLocations);
          return;
        }

        const { locations, isDistanceSorted } = await searchLocations({
          locationsArray: baseLocations,
          searchText: input,
        });

        setDisplayLocations(isDistanceSorted ? locations.slice(0, 5) : locations);
      }, 500),
    [baseLocations]
  );

  if (props.vehiclesSearched) {
    return (
      <>
        {/* <div style={{ height: 50 }}></div> */}
        <div className={styles.searchInfo}>
          <p className="font-medium">
            <span>{getLocationName(values.pickupServiceLocation)}</span>
            <div className={styles.rangeLine}></div>
            <span>{getLocationName(values.dropoffServiceLocation)}</span>
          </p>
          <p className="font-medium space-between">
            <span className="flex cross-center">
              <span>{d.fromISO(values.pickupDateTime).toFormat("dd MMM HH:mm")}</span>
              <div className={styles.rangeLine}></div>
              <span>{values.dropoffDateTime && d.fromISO(values.dropoffDateTime).toFormat("dd MMM HH:mm")}</span>
            </span>
            {props.resetSearch && <span onClick={props.resetSearch} className="bold underline flex cross-center">
              <EditIcon fontSize="inherit" />
              <span className="hover-opacity">Edit Search</span>
            </span>}
          </p>
        </div>
      </>
    )
  }

  return (
    <React.Fragment>
      <Formik
        enableReinitialize
        validationSchema={baseDetailsSchema}
        initialValues={values}
        onSubmit={(values, { setSubmitting }) => {
          clearSearchError();
          if (currentBranch && currentBranch.minBookingDurationOptIn && currentBranch.minBookingDuration) {
            const bookingDurationInHours = Math.abs(dayjs(values.dropoffDateTime).diff(dayjs(values.pickupDateTime), "hour"))
            if (currentBranch.minBookingDuration > bookingDurationInHours) {
              props.resetSearch()
              setSearchError({
                show: true,
                message: t("root:minBookingDurationMessage", { hours: currentBranch.minBookingDuration }),
              });
              return;
            }
          }
          if (currentBranch && currentBranch.maxBookingDurationOptIn && currentBranch.maxBookingDuration) {
            const bookingDurationInHours = Math.abs(dayjs(values.dropoffDateTime).diff(dayjs(values.pickupDateTime), "hour"))
            if (bookingDurationInHours > currentBranch.maxBookingDuration * 24) {
              props.resetSearch()
              setSearchError({
                show: true,
                message: t("root:maxBookingDurationMessage", { days: currentBranch.maxBookingDuration }),
              });
              return;
            }
          }
          if (!differentDropOffLocation) {
            values.dropoffServiceLocation = values.pickupServiceLocation;
          }
          const { isSubscription, activeSubscription, activeSubscriptionExpanded, ...rest } = values
          
          const params = new URLSearchParams(location.search);

          params.set("pickup_location", rest.pickupServiceLocation ?? "");
          params.set("dropoff_location", rest.dropoffServiceLocation ?? "");
          params.set("pickup_datetime", rest.pickupDateTime ?? "");
          params.set("dropoff_datetime", rest.dropoffDateTime ?? "");
          params.set("branch_id", rest.branchId ?? "");
          params.set("authorized_signatory", rest.authorizedSignatory ?? "");
          params.set("is_keyless", String(rest.isKeylessEntry ?? false));
          params.set("pco", String(rest.pcoNumber ?? false));

          if (rest.billingCycleDuration !== undefined) {
            params.set("billing_cycle", String(rest.billingCycleDuration));
          }

          if (rest.riskBasedPricingParams) {
            params.set("risk_based_driver_age", String(rest.riskBasedPricingParams.driverAge ?? 0));
            params.set("risk_based_licence_duration", String(rest.riskBasedPricingParams.licenceDuration ?? 0));
            params.set("risk_based_penalty_points", String(rest.riskBasedPricingParams.penaltyPoints ?? 0));
            params.set(
              "risk_based_insurance_claims",
              String(rest.riskBasedPricingParams.insuranceClaimsHistory ?? 0)
            );
          }

          const bookingType = params.get("bookingType");
          if (bookingType) params.set("bookingType", bookingType);

          history.push({
            pathname: location.pathname,
            search: params.toString(),
          });
          props.searchVehicles(rest);
          setSubmitting(false);
          setVehicleSearched(true);
        }}
      >
        {(formikProps) => (
          <Form className={styles.searchBox} style={{ marginTop: position || 70 }}>
            <Container className={classes.searchScreenGridContainer}>
              <Grid container className={styles.searcContainer}>
                <Grid container item xs={12} md={5} style={{ padding: '0 3px', position: "relative", order: 1 }}>
                  {
                    (!props.isBookingUpdate && allowSubscriptionBookings && website?.bookingTypes?.length > 0) &&
                    (website.bookingTypes.includes(BookingType.RENTAL) && website.bookingTypes.includes(BookingType.SUBSCRIPTION)) && (
                      <div className={styles.backToSelectionScreen}>
                        <Tooltip title= {t("root:backToBookingCategorySelection")}>
                          <IconButton
                            onClick={props.handleSelectionBack}
                          >
                            <ArrowBackIcon />
                          </IconButton>
                        </Tooltip>
                      </div>
                    )
                  }
                  <Grid item xs={12} sm={6}>
                    <div className={styles.searchParam}>
                      <div
                        className={styles.searchParamDisplay}
                        role="button"
                        onClick={() => handleSelectToggle(0)}
                      >
                        <p className="font-small bold">{t("root:pickUpLocation")}*</p>
                        <Autocomplete
                          clearOnBlur={false}
                          blurOnSelect={false}
                          selectOnFocus={false}
                          handleHomeEndKeys={false}
                          clearOnEscape
                          open={isSelectOpen[0]}
                          onClose={() => handleSelectToggle(0)}
                          popupIcon={null}
                          options={
                            branchData
                              ? [
                                currentLocationOption,
                                ...displayLocations.filter((location: IServiceLocation) => {
                                  if (website.organisation.crossLocationBookingEnabled && !values.id) {
                                    return true;
                                  }
                                  if (
                                    !values.branchId ||
                                    !props.isBookingUpdate ||
                                    (location?.branch as any)?.id === values.branchId
                                  ) {
                                    return true;
                                  }
                                  return false;
                                })
                              ] : []
                          }
                          value={
                            baseLocations.find(
                              (location) => location.id === values.pickupServiceLocation
                            ) || null
                          }
                          getOptionSelected={(option, value) => {
                            if (option.id === CURRENT_LOCATION_OPTION_ID) {
                              return false;
                            }
                            return Boolean(value) && option.id === value.id;
                          }}
                          getOptionLabel={(option) => {
                            if (option.id === CURRENT_LOCATION_OPTION_ID) {
                              return "";
                            }
                            return option.name;
                          }
                          }
                          filterOptions={(options) => options}
                          onInputChange={(event, value, reason) => {
                            if (reason !== "input") return;
                            handleZipcodeSearch(value);
                          }}
                          onChange={(event, newValue) => {
                            setVehicleSearched(false)
                            handleZipcodeSearch.cancel();
                            clearSearchError();
                            setRequiredFieldUpdated(true);
                            const value = newValue ? newValue.id : "";
                            if (!value) {
                              setValues(prev => ({
                                ...prev,
                                dropoffServiceLocation: "",
                                pickupServiceLocation: "",
                              }));
                              setDisplayLocations(baseLocations);
                              return;
                            }
                            if (value === CURRENT_LOCATION_OPTION_ID) {
                              event?.preventDefault();
                              setValues(prev => ({
                                ...prev,
                                dropoffServiceLocation: "",
                                pickupServiceLocation: "",
                              }));
                              return;
                            }
                            const branchId = getBranchId(value);
                            let shouldResetDateTime = false;
                            if (branchId !== values.branchId) {
                              shouldResetDateTime = true;
                            }
                            setValues(prev => ({
                              ...prev,
                              pickupServiceLocation: value,
                              dropoffServiceLocation: "",
                              branchId,
                              pickupDateTime: shouldResetDateTime ? "" : values.pickupDateTime,
                              dropoffDateTime: shouldResetDateTime ? "" : values.dropoffDateTime,
                            }));
                            setDisplayLocations(baseLocations);
                          }}
                          renderInput={(params) => {
                            const inputProps = params.inputProps as React.InputHTMLAttributes<HTMLInputElement>;
                            const inputValue = inputProps.value;
                            const inputOnChange = inputProps.onChange;
                            return (
                              <TextField
                                {...params}
                                variant="standard"
                                placeholder={getLocationName(values.pickupServiceLocation, false)}
                                InputProps={{
                                  ...params.InputProps,
                                  disableUnderline: true,
                                  endAdornment: Boolean(inputValue) ? (
                                    <InputAdornment position="end">
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleZipcodeSearch.cancel();
                                          setValues(prev => ({
                                            ...prev,
                                            pickupServiceLocation: "",
                                            dropoffServiceLocation: "",
                                          }));
                                          setDisplayLocations(baseLocations);
                                          inputOnChange?.({
                                            target: { value: "" }
                                          } as any);
                                          handleSelectToggle(0, false);
                                        }}
                                      >
                                        <Clear fontSize="small" />
                                      </IconButton>
                                    </InputAdornment>
                                  ) : null,
                                  style: {
                                    padding: 0,
                                    fontSize: "inherit"
                                  }
                                }}
                                inputProps={{
                                  ...params.inputProps,
                                  style: {
                                    padding: 0,
                                    fontSize: "inherit"
                                  }
                                }}
                              />
                            );
                          }}
                          renderOption={(option) => {
                            if (option.id === CURRENT_LOCATION_OPTION_ID) {
                              return (
                                <>
                                  <Typography
                                    variant="body1"
                                    style={{
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 6,
                                      padding: "6px 12px"
                                    }}
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (!navigator.geolocation) return;
                                      handleSelectToggle(0, true);
                                      navigator.geolocation.getCurrentPosition(
                                        async (position) => {
                                          const { latitude, longitude } = position.coords;
                                          const { locations } =
                                            await searchLocations({
                                              locationsArray: baseLocations,
                                              coordinates: {
                                                lat: latitude,
                                                lon: longitude,
                                              },
                                            });
                                          setDisplayLocations(locations.slice(0, 5));
                                          handleSelectToggle(0, true);
                                        },
                                        (error) => {
                                          console.error("renderOption Geolocation error:", error);
                                          handleSelectToggle(0, true);
                                        }
                                      );
                                    }}
                                  >
                                    <NearMeTwoTone fontSize="small" color="action" />
                                    {option.name}
                                  </Typography>
                                </>
                              )
                            }
                            return (
                              <Tooltip
                                interactive
                                placement="right"
                                arrow
                                title={<LocationTooltipCard location={option} />}
                              >
                                <Typography variant="body1">
                                  {option.name}
                                </Typography>
                              </Tooltip>
                            );
                          }}
                          PopperComponent={(props) => (
                            <Popper
                              {...props}
                              style={{
                                width: 270,
                              }}
                            />
                          )}
                        />
                      </div>
                    </div>

                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <div className={styles.searchParam}>
                      <div className={styles.dropInputCheck}>
                        <Checkbox
                          color="secondary"
                          onChange={() => {
                            clearSearchError();
                            setVehicleSearched(false)
                            differentDropOffLocation && setValues({
                              ...values,
                              dropoffServiceLocation: ""
                            });
                            setDifferentDropOffLocation(
                              !differentDropOffLocation
                            )
                            setRequiredFieldUpdated(true)
                            setSelectOpen(isSelectOpenInitial)
                            setDisplayLocations(baseLocations)
                          }}
                          checked={differentDropOffLocation}
                          checkedIcon={<CheckBoxIcon fontSize="small" className={styles.dropInputCheckBox} />}
                          icon={<CheckBoxOutlineBlankIcon fontSize="small" className={styles.dropInputCheckBox} />}
                          inputProps={{ 'aria-label': 'primary checkbox' }}
                        />
                      </div>
                      <div
                        className={cx(styles.searchParamDisplay, !differentDropOffLocation && styles.disabled)}
                        style={{ paddingLeft: 40 }}
                        role="button"
                        onClick={() => differentDropOffLocation && handleSelectToggle(1)}
                      >
                        <p className="font-small bold" style={{ opacity: differentDropOffLocation ? 1 : 0.3 }}>
                          {t("root:returnLocation")}
                        </p>
                        <Autocomplete
                          disabled={!differentDropOffLocation}
                          clearOnBlur={false}
                          blurOnSelect={false}
                          selectOnFocus={false}
                          handleHomeEndKeys={false}
                          clearOnEscape
                          open={differentDropOffLocation && isSelectOpen[1]}
                          onClose={() => handleSelectToggle(1)}
                          popupIcon={null}
                          options={
                            branchData
                              ? [
                                currentLocationOption,
                                ...displayLocations.filter((location: IServiceLocation) => {
                                  if (website.organisation.crossLocationBookingEnabled) return true;
                                  if (
                                    !values.branchId ||
                                    (location?.branch as any)?.id === values.branchId
                                  )
                                    return true;
                                  return false;
                                }),
                              ]
                              : []
                          }
                          value={
                            baseLocations.find(
                              (location) => location.id === values.dropoffServiceLocation
                            ) || null
                          }
                          getOptionSelected={(option, value) => {
                            if (option.id === CURRENT_LOCATION_OPTION_ID) return false;
                            return Boolean(value) && option.id === value.id;
                          }}
                          getOptionLabel={(option) => {
                            if (option.id === CURRENT_LOCATION_OPTION_ID) return "";
                            return option.name;
                          }}
                          filterOptions={(options) => options}
                          onInputChange={(event, value, reason) => {
                            if (reason !== "input") return;
                            handleZipcodeSearch(value);
                          }}
                          onChange={(event, newValue) => {
                            setVehicleSearched(false)
                            handleZipcodeSearch.cancel();
                            clearSearchError();
                            setRequiredFieldUpdated(true);

                            if (!newValue) {
                              setValues(prev => ({
                                ...prev,
                                dropoffServiceLocation: "",
                              }));
                              setDisplayLocations(baseLocations);
                              return;
                            }

                            if (newValue.id === CURRENT_LOCATION_OPTION_ID) {
                              event?.preventDefault();
                              setValues(prev => ({
                                ...prev,
                                dropoffServiceLocation: "",
                              }));
                              return;
                            }

                            setDifferentDropOffLocation(true);
                            setValues(prev => ({
                              ...prev,
                              dropoffServiceLocation: newValue.id,
                            }));
                            setDisplayLocations(baseLocations);
                          }}
                          renderInput={(params) => {
                            const inputProps =
                              params.inputProps as React.InputHTMLAttributes<HTMLInputElement>;
                            const inputValue = inputProps.value;
                            const inputOnChange = inputProps.onChange;

                            return (
                              <TextField
                                {...params}
                                variant="standard"
                                placeholder={getLocationName(values.dropoffServiceLocation, false)}
                                InputProps={{
                                  ...params.InputProps,
                                  disableUnderline: true,
                                  endAdornment: Boolean(inputValue) ? (
                                    <InputAdornment position="end">
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleZipcodeSearch.cancel();
                                          setValues(prev => ({
                                            ...prev,
                                            dropoffServiceLocation: "",
                                          }));
                                          setDisplayLocations(baseLocations);
                                          inputOnChange?.({ target: { value: "" } } as any);
                                          handleSelectToggle(1, false)
                                        }}
                                        disabled={!differentDropOffLocation}
                                      >
                                        <Clear fontSize="small" />
                                      </IconButton>
                                    </InputAdornment>
                                  ) : null,
                                  style: {
                                    padding: 0,
                                    fontSize: "inherit",
                                  },
                                }}
                                inputProps={{
                                  ...params.inputProps,
                                  style: {
                                    padding: 0,
                                    fontSize: "inherit",
                                  },
                                }}
                              />
                            );
                          }}
                          renderOption={(option) => {
                            if (option.id === CURRENT_LOCATION_OPTION_ID) {
                              return (
                                <Typography
                                  variant="body1"
                                  style={{
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                    padding: "6px 12px",
                                  }}
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (!navigator.geolocation) return;
                                    handleSelectToggle(1, true);
                                    navigator.geolocation.getCurrentPosition(
                                      async (position) => {
                                        const { latitude, longitude } = position.coords;
                                        const { locations } = await searchLocations({
                                          locationsArray: baseLocations,
                                          coordinates: { lat: latitude, lon: longitude },
                                        });
                                        setDisplayLocations(locations.slice(0, 5));
                                        handleSelectToggle(1, true);
                                      },
                                      (error) => {
                                        console.error("Drop-off geolocation error:", error);
                                        handleSelectToggle(1, true);
                                      }
                                    );
                                  }}
                                >
                                  <NearMeTwoTone fontSize="small" color="action" />
                                  {option.name}
                                </Typography>
                              );
                            }
                            return (
                              <Tooltip
                                interactive
                                placement="right"
                                arrow
                                title={<LocationTooltipCard location={option} />}
                              >
                                <Typography variant="body1">
                                  {option.name}
                                </Typography>
                              </Tooltip>
                            );
                          }}
                          PopperComponent={(props) => (
                            <Popper
                              {...props}
                              style={{
                                marginRight: 25,
                                width: 270,
                              }}
                            />
                          )}
                        />
                      </div>
                    </div>
                  </Grid>
                </Grid>
                <Grid container item xs={12} md={3} style={{ padding: '0 3px', order: 2}}>
                  <Grid item xs={6}>
                    <div className={styles.searchParam}>
                      <label htmlFor="pickUpDateInput" className={styles.searchParamDisplay} role="button">
                        <p className="font-small bold">{t("root:pickUpDate")}</p>
                        <div className="flex space-between">
                          <span>{values.pickupDateTime ? d.fromISO(values.pickupDateTime).toFormat('dd MMM yy') : t("misc:select")}</span>
                          <ExpandMoreIcon style={{ fontSize: 20 }} />
                        </div>
                      </label>
                      <FlatPickerBar
                        handleDateChange={(value: Date) => handlePickUpDate(value)}
                        identifier={"pickupdateTime"}
                        name={"pickupdateTime"}
                        value={getLocalizedDateFormat(country, values.pickupDateTime, DATE_TYPE.CONDENSED)}
                        minDate={getOperatingHours("PICKUP", minPickupDateTime)[1]
                          ? getLocalizedDateFormat(country, minPickupDateTime.toUTC().toISO(), DATE_TYPE.CONDENSED)
                          : getLocalizedDateFormat(country, minPickupDateTime.plus({ days: 1 }).toUTC().toISO(), DATE_TYPE.CONDENSED)}
                        maxDate={maxPickupDateTime
                          ? getLocalizedDateFormat(country, maxPickupDateTime.toUTC().toISO(), DATE_TYPE.CONDENSED)
                          : undefined}
                        disabledDates={getDisabledDates("PICKUP")}
                        timeZone={currentBranch?.timeZone}
                        render={({ defaultValue, value, ...props }: any, ref: any) => (
                          <input id="pickUpDateInput" {...props} defaultValue={defaultValue} ref={ref} className={styles.transparentInput} />
                        )}
                        country={country}
                      />

                    </div>
                  </Grid>
                  <Grid item xs={6}>
                    <div className={styles.searchParam}>
                      <div className={styles.searchParamDisplay} role="button" onClick={() => handleSelectToggle(3)}>
                        <p className="font-small bold">{t("root:pickUpTime")}</p>
                        <div className="flex space-between">
                          <span>{values.pickupDateTime ? d.fromISO(values.pickupDateTime).toFormat("hh:mm a") : t("misc:select") }</span>
                          <ExpandMoreIcon style={{ fontSize: 20 }} />
                        </div>
                      </div>
                      <Field
                        id="pickup-select-outlined"
                        required
                        name={"pickupTime"}
                        fullWidth
                      >
                        {({ field }: FieldProps) => (
                          <Select
                            {...field}
                            input={<PlainInput />}
                            value={values.pickupDateTime ? d.fromISO(values.pickupDateTime).toFormat("HH:mm") : "00:00"}
                            disabled={!values.pickupDateTime}
                            onChange={handlePickUpTime}
                            onClose={() => handleSelectToggle(3)}
                            open={isSelectOpen[3]}
                            MenuProps={{
                              style: {
                                maxHeight: 500
                              }
                            }}
                          >
                            {getTimesArray(operatingHours.pickUp).map((hour, index) => {
                              return (
                                <MenuItem value={hour} key={index}>
                                  {d.fromFormat(hour, "HH:mm").toFormat("hh:mm a")}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        )}
                      </Field>
                    </div>
                  </Grid>
                </Grid>
                <Grid container item xs={12} md={3} style={{ padding: '0 3px', order: 3 }}>
                  <Grid item xs={6}>
                    <div className={styles.searchParam}>
                      <label htmlFor="dropOffDateInput" className={cx(styles.searchParamDisplay, 's-p-d')} role="button">
                        <p className="font-small bold">{t("root:dropOffDate")}</p>
                        <div className="flex space-between">
                          <span>{values.dropoffDateTime ? d.fromISO(values.dropoffDateTime).toFormat('dd MMM yy') : t("misc:select")}</span>
                          <ExpandMoreIcon style={{ fontSize: 20 }} />
                        </div>
                      </label>
                      <FlatPickerBar
                        handleDateChange={(value: Date) => handleDropoffDate(value)}
                        identifier={"dropoffDateTime"}
                        name={"dropoffDateTime"}
                        value={getLocalizedDateFormat(country, values.dropoffDateTime || "", DATE_TYPE.CONDENSED)}
                        timeZone={currentBranch?.timeZone}
                        minDate={
                          values.pickupDateTime
                            ? getLocalizedDateFormat(country, d.fromISO(values.pickupDateTime)
                              .plus({
                                hours: currentBranch?.minBookingDurationOptIn && currentBranch.minBookingDuration
                                  ? currentBranch?.minBookingDuration
                                  : 0
                              })
                              .toUTC()
                              .toISO(), DATE_TYPE.CONDENSED)
                            : getLocalizedDateFormat(country, d.now().plus({ days: 1 }).toUTC().toISO(), DATE_TYPE.CONDENSED)
                        }
                        maxDate={
                          values.pickupDateTime &&
                          currentBranch?.maxBookingDurationOptIn &&
                          currentBranch.maxBookingDuration
                            ? getLocalizedDateFormat(country, d.fromISO(values.pickupDateTime)
                              .plus({ days: currentBranch.maxBookingDuration })
                              .toUTC()
                              .toISO(), DATE_TYPE.CONDENSED)
                            : undefined
                        }
                        disabledDates={getDisabledDates("DROPOFF")}
                        render={({ defaultValue, value, ...props }: any, ref: any) => (
                          <input id="dropOffDateInput" {...props} defaultValue={defaultValue} ref={ref} className={styles.transparentInput} />
                        )}
                        country={country}
                      />

                    </div>
                  </Grid>
                  <Grid item xs={6}>
                    <div className={styles.searchParam}>
                      <div className={styles.searchParamDisplay} role="button" onClick={() => handleSelectToggle(5)}>
                        <p className="font-small bold">{t("root:dropOffTime")}</p>
                        <div className="flex space-between">
                          <span>{values.dropoffDateTime ? d.fromISO(values.dropoffDateTime).toFormat("hh:mm a") : t("misc:select")}</span>
                          <ExpandMoreIcon style={{ fontSize: 20 }} />
                        </div>
                      </div>
                      <Field
                        required
                        select
                        name={"dropOffTime"}
                        fullWidth
                      >
                        {({ field }: FieldProps) => (
                          <Select
                            {...field}
                            input={<PlainInput />}
                            value={values.dropoffDateTime ? d.fromISO(values.dropoffDateTime).toFormat("HH:mm") : "00:00"}
                            disabled={!values.dropoffDateTime}
                            onChange={(event: any) => {
                              clearSearchError();
                              const dropoffDateTime = setTimeOnDate(d.fromISO(values.dropoffDateTime || ""), hourToNumber(event.target.value))
                                .toUTC()
                                .toISO();
                              setValues({
                                ...values,
                                dropoffDateTime: dropoffDateTime
                              });
                              setRequiredFieldUpdated(true)
                              setVehicleSearched(false)
                            }}
                            onClose={() => handleSelectToggle(5)}
                            open={isSelectOpen[5]}
                            MenuProps={{
                              style: {
                                maxHeight: 500
                              }
                            }}
                          >
                            {getTimesArray(operatingHours.dropOff).map((hour, index) => {
                              return (
                                <MenuItem value={hour}>
                                  {d.fromFormat(hour, "HH:mm").toFormat("hh:mm a")}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        )}
                      </Field>
                    </div>
                  </Grid>
                </Grid>
                <Grid container item xs={12} md={1} style={{ padding: '0 3px', marginLeft: 'auto', order: isMobile ? 6 : 4 }}>
                  <Grid item xs={12}>
                    <div className={styles.searchButtonWrap}>
                      <button
                        className={styles.searchButton}
                        aria-label= {("root:search")}
                        type="submit"
                        disabled={
                          !values.pickupServiceLocation ||
                          !values.pickupDateTime ||
                          !values.billingCycleDuration || !values.dropoffDateTime ||
                          (differentDropOffLocation && !values.dropoffServiceLocation) || 
                          (!!user?.businesscustomers && !values.authorizedSignatory) ||
                          vehicleSearched ||
                          d.fromISO(values.dropoffDateTime) <= d.fromISO(values.pickupDateTime) ||
                          searchDisabled
                        }
                      >
                        <Hidden smDown>
                          <SearchIcon fontSize="inherit" color="inherit" />
                        </Hidden>
                        <Hidden mdUp>
                          <span className="font-regular bold">{t("root:searchVehicles")}</span>
                        </Hidden>
                      </button>
                    </div>
                  </Grid>
                </Grid>
                {user?.businesscustomers &&
                  <Grid item xs={12} md={3} style={{order: isMobile ? 3 : 5}} >
                    <div className={styles.searchParam}>
                      <div className={styles.searchParamDisplay} role="button" onClick={() => handleSelectToggle(7)}>
                        <p className="font-small bold">{getLocalizedAuthorisedSignatorySyntex(country)} *</p>
                        <div className="flex space-between">
                          <span>{getNameForAuthorizedSignatory(values.authorizedSignatory,user) ?? t('misc:select')}</span>
                          <ExpandMoreIcon style={{ fontSize: 20 }} />
                        </div>
                      </div>
                      <Field
                        required
                        select
                        name={"authorisedSignatory"}
                        fullWidth
                      >
                        {({ field }: FieldProps) => (
                          <Select
                            {...field}
                            input={<PlainInput />}
                            value={values.authorizedSignatory}
                                onChange={(event: any) => {
                              clearSearchError();
                              const authorisedSignatory = event.target.value
                                setValues({
                                ...values,
                                authorizedSignatory: authorisedSignatory
                              });
                              setRequiredFieldUpdated(true)
                              setVehicleSearched(false)
                            }}
                            onClose={() => handleSelectToggle(7)}
                            open={isSelectOpen[7]}
                            MenuProps={{
                              style: {
                                maxHeight: 500
                              }
                            }}
                          >
                            {user?.businesscustomers?.authorizedSignatories.map((ele,idx) => {
                              return (
                                <MenuItem value={ele.id}>
                                  {`${ele.firstName} ${ele.lastName}`}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        )}
                      </Field>
                    </div>
                  </Grid>
                }
                {riskBasedPricingEnabled && (
                  <>
                    {/* Driver Age */}
                    <Grid item xs={12} md={2} style={{ order: isMobile ? 4 : 6 }}>
                      <div className={styles.searchParam}>
                        <div className={styles.searchParamDisplay}>
                          <p className="font-small bold">{t('root:driverAge')}</p>
                          <InputBase
                            type="number"
                            fullWidth
                            value={formikProps.values?.riskBasedPricingParams?.driverAge ?? 0}
                            inputProps={{ min: 0 }}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                              const value = event.target.value;
                              if (value === "") {
                                formikProps.setFieldValue("riskBasedPricingParams.driverAge", 0);
                                return;
                              }
                              const numericValue = Number(value);
                              if (numericValue < 0) return;
                              formikProps.setFieldValue("riskBasedPricingParams.driverAge", numericValue);
                              handleRiskBasedPricingParamsChange("driverAge", numericValue);
                            }}
                          />
                        </div>
                      </div>
                    </Grid>

                    {/* Licence Duration */}
                    <Grid item xs={12} md={2} style={{ order: isMobile ? 4 : 7 }}>
                      <div className={styles.searchParam}>
                        <div className={styles.searchParamDisplay}>
                          <p className="font-small bold">{t('root:licenceDuration')}</p>
                          <InputBase
                            type="number"
                            fullWidth
                            value={formikProps.values?.riskBasedPricingParams?.licenceDuration ?? 0}
                            inputProps={{ min: 0 }}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                              const value = event.target.value;
                              if (value === "") {
                                formikProps.setFieldValue("riskBasedPricingParams.licenceDuration", 0);
                                return;
                              }
                              const numericValue = Number(value);
                              if (numericValue < 0) return;
                              formikProps.setFieldValue("riskBasedPricingParams.licenceDuration", numericValue);
                              handleRiskBasedPricingParamsChange("licenceDuration", numericValue);
                            }}
                          />
                        </div>
                      </div>
                    </Grid>

                    {/* Penalty Points */}
                    <Grid item xs={12} md={2} style={{ order: isMobile ? 4 : 8 }}>
                      <div className={styles.searchParam}>
                        <div className={styles.searchParamDisplay}>
                          <p className="font-small bold">{t('root:penaltyPoints')}</p>
                          <InputBase
                            type="number"
                            fullWidth
                            value={formikProps.values?.riskBasedPricingParams?.penaltyPoints ?? 0}
                            inputProps={{ min: 0 }}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                              const value = event.target.value;
                              if (value === "") {
                                formikProps.setFieldValue("riskBasedPricingParams.penaltyPoints", 0);
                                return;
                              }
                              const numericValue = Number(value);
                              if (numericValue < 0) return;
                              formikProps.setFieldValue("riskBasedPricingParams.penaltyPoints", numericValue);
                              handleRiskBasedPricingParamsChange("penaltyPoints", numericValue);
                            }}
                          />
                        </div>
                      </div>
                    </Grid>

                    {/* Insurance Claims History */}
                    <Grid item xs={12} md={2} style={{ order: isMobile ? 4 : 9 }}>
                      <div className={styles.searchParam}>
                        <div className={styles.searchParamDisplay}>
                          <p className="font-small bold">{t('root:insuranceClaimsHistory')}</p>
                          <InputBase
                            type="number"
                            fullWidth
                            value={formikProps.values?.riskBasedPricingParams?.insuranceClaimsHistory ?? 0}
                            inputProps={{ min: 0 }}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                              const value = event.target.value;
                              if (value === "") {
                                formikProps.setFieldValue("riskBasedPricingParams.insuranceClaimsHistory", 0);
                                return;
                              }
                              const numericValue = Number(value);
                              if (numericValue < 0) return;
                              formikProps.setFieldValue("riskBasedPricingParams.insuranceClaimsHistory", numericValue);
                              handleRiskBasedPricingParamsChange("insuranceClaimsHistory", numericValue);
                            }}
                          />
                        </div>
                      </div>
                    </Grid>
                  </>
                )}
                
                <Box style={{ width: "100%", padding: '0 3px', borderRadius: 2 }}>
                  <Collapse in={showSearchError.show}>
                    <Alert severity="error">
                      <Typography variant="body1">
                        {showSearchError.message}
                      </Typography>
                    </Alert>
                  </Collapse>
                </Box>
              </Grid>
            </Container>
          </Form>
        )}
      </Formik>
    </React.Fragment >
  );
};

export default BaseDetails;

const LocationTooltipCard = ({ location }: { location: IServiceLocation }) => {
  const { name, address, branch } = location;
  const branchObj = typeof branch === "object" ? branch : undefined;

  const phoneNumber = branchObj?.phoneNumber?.phone || "-";

  return (
    <Card elevation={4} style={{ maxWidth: 360 }}>
      <CardContent style={{ padding: 14 }}>
        {/* TITLE */}
        <Typography
          variant="subtitle1"
          style={{ fontWeight: 700, marginBottom: 2 }}
        >
          {name}
        </Typography>

        {/* BRANCH NAME */}
        {branchObj?.name && (
          <Typography variant="body2" color="textSecondary">
            {branchObj.name}
          </Typography>
        )}

        <Divider style={{ margin: "10px 0" }} />

        {/* ADDRESS */}
        <>
          <Typography
            variant="caption"
            color="textSecondary"
            style={{ fontWeight: 600 }}
          >
            Address
          </Typography>

          <Typography variant="body2">
            {address.fullAddress ||
              [
                address.street,
                address.city,
                address.state,
                address.zipcode,
                address.country,
              ]
                .filter(Boolean)
                .join(", ")}
          </Typography>
        </>

        {/* PHONE */}
        <>
        <Typography variant="body2" style={{ marginTop: 10 }} />
          <Typography
            variant="caption"
            color="textSecondary"
            style={{ fontWeight: 600 }}
          >
            Phone
          </Typography>

          <Typography variant="body2">
            {phoneNumber}
          </Typography>
        </>
      </CardContent>
    </Card>
  );
};